import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { TogglerInput } from 'components/form/TogglerInput.component';
import Tooltip from 'components/generic/Tooltip.component';
import S from 'constants/string.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useTip from 'hooks/useTip.hook';

import { feConsultationFormToggleStatus } from 'settings/consultation-forms/consultationForms.action';
import { FORM_AUTOMATION_TYPES, FORM_AUTOMATION_TYPES_LABELS, FORM_STATUS } from 'settings/consultation-forms/consultationForms.const';
import { FormRowManageDropdown } from 'settings/consultation-forms/list/FormRowManageDropdown.component';
import { DeactivateFormModal } from 'settings/consultation-forms/modals/DeactivateFormModal.component';
import { ReactivateFormModal } from 'settings/consultation-forms/modals/ReactivateFormModal.component';

import CN from './FormRow.module.scss';

const STATUS_CLASS = {
  [FORM_STATUS.ACTIVE]: 'statusActive',
  [FORM_STATUS.INACTIVE]: 'statusInactive',
  [FORM_STATUS.DRAFT]: 'statusDraft',
};

export const FormRow = ({ form, isDarkMode = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useIsMobileViewport();

  const { val: isDeactivateOpen, yay: openDeactivate, nay: closeDeactivate } = useTip(false);
  const { val: isReactivateOpen, yay: openReactivate, nay: closeReactivate } = useTip(false);

  const isDraft = form.status === FORM_STATUS.DRAFT;
  const isActive = form.status === FORM_STATUS.ACTIVE;
  const isInactive = form.status === FORM_STATUS.INACTIVE;
  const hasAutomationType = form.automationType;
  const isAutomatic = hasAutomationType && form.automationType !== FORM_AUTOMATION_TYPES.MANUAL;
  const automationLabel = FORM_AUTOMATION_TYPES_LABELS[form.automationType];

  const handleToggleChange = () => {
    if (isDraft) return;
    if (isActive) {
      openDeactivate();
    } else {
      openReactivate();
    }
  };

  const handleDeactivateConfirm = () => {
    dispatch(feConsultationFormToggleStatus({ formId: form.id, newStatus: FORM_STATUS.INACTIVE }));
    closeDeactivate();
  };

  const handleReactivateConfirm = () => {
    dispatch(feConsultationFormToggleStatus({ formId: form.id, newStatus: FORM_STATUS.ACTIVE }));
    closeReactivate();
  };

  return (
    <>
      <div data-bem="FormsTable__row" className={cn(CN.row, { [CN.rowDark]: isDarkMode })}>
        <div className={CN.formInfo}>
          <div className={CN.formIcon}>{form.icon || S.clipboardDocument}</div>
          <div className={CN.formDetails}>
            <div className={CN.formNameRow}>
              <span className={CN.formName}>{form.name}</span>
              <span className={cn(CN.statusPill, CN[STATUS_CLASS[form.status]])}>{t(`consultationForms.status.${form.status}`)}</span>
            </div>

            {hasAutomationType &&
              (isAutomatic ? (
                <div className={cn(CN.automationTypeWrapper, { [CN.automationTypeInactive]: isInactive })}>
                  <div className={CN.automationTypeContent}>
                    <span className={CN.automationTypeLabel}>
                      ⏱️{S.space}
                      {t(`consultationForms.automation.automation`)}
                    </span>
                    {!isMobile && S.dot}
                  </div>
                  <div className={CN.automationTypeContent}>
                    {automationLabel && <span className={CN.automationTypeText}>{t(automationLabel)}</span>}
                    {isInactive && (
                      <span className={CN.automationTypeText}>
                        {!isMobile && (
                          <>
                            {S.dot}
                            {S.space}
                          </>
                        )}
                        {t(`consultationForms.paused`)}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className={cn(CN.automationTypeContent, { [CN.automationTypeInactive]: isInactive })}>
                  <span className={CN.manualAutomationTypeLabel}>
                    ✋{S.space}
                    {t(`consultationForms.automation.manual`)}
                  </span>
                </div>
              ))}

            {form.ruleTags && form.ruleTags.length > 0 ? (
              <div className={CN.ruleTags}>
                {form.ruleTags.map((tag) => (
                  <span key={`${tag.emoji}-${tag.label}`} className={CN.ruleTag}>
                    {tag.emoji} {tag.label}
                  </span>
                ))}
              </div>
            ) : (
              <div className={CN.automationTypeContent}>
                <span className={CN.noRulesText}>
                  {S.waitTriangle}
                  {S.space}
                  {t(`consultationForms.noRules`)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={CN.statusCell}>
          <TogglerInput
            data-tag={`FormsTable__toggle--${form.id}`}
            checked={isActive}
            disabled={isDraft}
            onChange={handleToggleChange}
            aria-label={`${t('consultationForms.tableHeaders.status')}: ${form.name}`}
          />
          {isDraft && (
            <Tooltip content={t('consultationForms.draftTooltip')}>
              <InformationCircleIcon className={CN.informationCircleIcon} />
            </Tooltip>
          )}
        </div>

        <div className={CN.manageCell}>
          <FormRowManageDropdown form={form} />
        </div>
      </div>

      <DeactivateFormModal isOpen={isDeactivateOpen} onClose={closeDeactivate} onConfirm={handleDeactivateConfirm} formName={form.name} />

      <ReactivateFormModal isOpen={isReactivateOpen} onClose={closeReactivate} onConfirm={handleReactivateConfirm} formName={form.name} ruleTags={form.ruleTags} />
    </>
  );
};
